import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { OptimisticMessage } from "types";
import { isOptimisticMessage } from "./messageMetadata";

/**
 * Merge a freshly-fetched server page into the currently-displayed message list
 * WITHOUT remounting bubbles or jumping scroll. This is what makes the
 * cache-first "open a chat → see last messages instantly, then update" flow
 * (Telegram-style) safe — see MESSAGES_SCROLL_FLOW.md for the invariants.
 *
 * Both `prev` and the return value are **DESC** (newest first, index 0 = newest),
 * matching the convention everywhere in the message controllers. The server
 * returns DESC too; the IndexedDB cache returns ASC, so the hydration step
 * reverses it once and we re-sort here by id-descending (server ids are
 * monotonic — the single ordering authority).
 *
 * Guarantees:
 *  - Unchanged rows keep their EXACT object reference (so Virtuoso doesn't
 *    re-measure → no flicker).
 *  - `_optimisticId` is carried forward onto refreshed server rows so the
 *    React key `msg-${_optimisticId || id}` never flips → no bubble remount.
 *  - Pending optimistic placeholders (have `_optimisticId` AND `_status`) are
 *    never touched and stay at the front (they are always the newest) — UNLESS
 *    their real twin appears in the fetched page (matched by `clientMessageId`),
 *    in which case the placeholder is evicted and its `_optimisticId` is carried
 *    onto the real row so the React key stays stable (no remount). This is what
 *    self-heals a placeholder that never resolved via the send response (e.g. a
 *    lost response) — without it, an orphaned "sending" ghost re-pins to the
 *    bottom of the chat on every refetch.
 *  - A cached/old row the server omitted is treated as deleted ONLY when its id
 *    falls strictly inside the fetched page's [minId, maxId] window. Rows older
 *    or newer than the window are just out-of-page and are kept.
 */
export function mergeMessages(prev: Message[], incoming: Message[]): Message[] {
  // 1. Keep pending optimistic placeholders aside — the server can't know them.
  //    (resolveOptimistic owns the placeholder→real swap; merge must not race it.)
  let pendingOptimistic = prev.filter((m): m is OptimisticMessage => isOptimisticMessage(m));
  const prevSettled = prev.filter((m) => !isOptimisticMessage(m));

  // Index pending placeholders by their idempotency key so an incoming server
  // row carrying the same clientMessageId can reclaim (and evict) it.
  const optByClientId = new Map<string, OptimisticMessage>();
  for (const p of pendingOptimistic) {
    if (p.clientMessageId) optByClientId.set(String(p.clientMessageId), p);
  }
  const evictedOptIds = new Set<string>();

  const prevById = new Map<string, Message>();
  for (const m of prevSettled) prevById.set(String(m.id), m);

  const seen = new Set<string>();
  const merged: Message[] = [];

  // 2. Every incoming row is the source of truth (covers server-side edits).
  for (const srv of incoming) {
    const id = String(srv.id);
    seen.add(id);
    const old = prevById.get(id);
    // Inherit `_optimisticId` for key stability, either from a same-id prev row
    // (already-resolved case) or from a pending placeholder matched by
    // clientMessageId (orphan case — the placeholder never resolved).
    let optId = old ? (old as { _optimisticId?: string })._optimisticId : undefined;
    if (!optId && srv.clientMessageId) {
      const placeholder = optByClientId.get(String(srv.clientMessageId));
      if (placeholder) {
        optId = placeholder._optimisticId;
        evictedOptIds.add(placeholder._optimisticId);
      }
    }
    const candidate: Message = optId ? ({ ...srv, _optimisticId: optId } as Message) : srv;
    // Reuse the old reference when nothing render-relevant changed.
    merged.push(old && sameRenderState(old, candidate) ? old : candidate);
  }

  // 3. Keep prev rows the server didn't return, unless they were deleted
  //    (id strictly inside the fetched window).
  for (const old of prevSettled) {
    const id = String(old.id);
    if (seen.has(id)) continue;
    if (isWithinWindow(old, incoming)) continue; // server omitted an in-window id → deleted
    merged.push(old);
  }

  // 4. Sort DESC by numeric id (fallback: time desc), drop placeholders whose
  //    real twin was merged in, and re-attach the rest at the front.
  merged.sort(cmpDesc);
  if (evictedOptIds.size) {
    pendingOptimistic = pendingOptimistic.filter((p) => !evictedOptIds.has(p._optimisticId));
  }
  return pendingOptimistic.length ? [...pendingOptimistic, ...merged] : merged;
}

/**
 * Smallest (oldest) REAL message id in a list — the cursor for the next
 * scroll-up page (`lessThanId`). Optimistic placeholders carry non-numeric ids
 * ("_opt_…") and are skipped so they can never become the cursor. Returns
 * `undefined` for an empty/all-optimistic list (→ caller fetches the newest page).
 */
export function oldestRealId(msgs: Message[]): string | undefined {
  let min: number | undefined;
  for (const m of msgs) {
    const n = Number(m.id);
    if (!Number.isFinite(n)) continue;
    if (min === undefined || n < min) min = n;
  }
  return min === undefined ? undefined : String(min);
}

/** True when `msg.id` lies strictly between the min and max id of the page. */
function isWithinWindow(msg: Message, page: Message[]): boolean {
  if (page.length === 0) return false; // an empty page tells us nothing
  const id = Number(msg.id);
  if (isNaN(id)) return false;
  let min = Infinity;
  let max = -Infinity;
  for (const m of page) {
    const n = Number(m.id);
    if (isNaN(n)) continue;
    if (n < min) min = n;
    if (n > max) max = n;
  }
  return id > min && id < max;
}

function cmpDesc(a: Message, b: Message): number {
  const an = Number(a.id);
  const bn = Number(b.id);
  if (!isNaN(an) && !isNaN(bn) && an !== bn) return bn - an;
  const at = Date.parse(a.time) || 0;
  const bt = Date.parse(b.time) || 0;
  if (at !== bt) return bt - at;
  return String(b.id).localeCompare(String(a.id));
}

/**
 * Compares the fields that drive how a bubble renders. When equal, the merge
 * reuses the previous object so React/Virtuoso see an identical item.
 * JSON.stringify is used for the structured fields — cheap at message-list
 * sizes and avoids depending on the exact attachment/reaction shape.
 */
function sameRenderState(a: Message, b: Message): boolean {
  const aa = a as any;
  const bb = b as any;
  return (
    a.text === b.text &&
    a.time === b.time &&
    aa._status === bb._status &&
    aa._optimisticId === bb._optimisticId &&
    a.attachmentAvailable === b.attachmentAvailable &&
    a.attachmentPricePackageId === b.attachmentPricePackageId &&
    String(a.senderId) === String(b.senderId) &&
    JSON.stringify(a.attachment) === JSON.stringify(b.attachment) &&
    JSON.stringify(a.attachmentPurchasers) === JSON.stringify(b.attachmentPurchasers) &&
    JSON.stringify(a.details) === JSON.stringify(b.details) &&
    JSON.stringify(a.link) === JSON.stringify(b.link) &&
    JSON.stringify(aa.reactions) === JSON.stringify(bb.reactions)
  );
}
